import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Present.css';

function BBanco({ usuario }) {
    const [bancos, setBancos] = useState([]);
    const [cuentasContables, setCuentasContables] = useState([]);
    const [monedas, setMonedas] = useState([]);
    const [formulario, setFormulario] = useState({
        idBanco: '',
        nroCta: '',
        nomBco: '',
        nomCta: '',
        idMoneda: '',
        saldo: '0.00',
        obs: '',
        idOperador: usuario || ''
    });
    const [modoEdicion, setModoEdicion] = useState(false);
    const [mostrarModal, setMostrarModal] = useState(false);
    const [errores, setErrores] = useState({});
    const [busqueda, setBusqueda] = useState('');

    useEffect(() => {
        cargarBancos();
        cargarCuentasImputables();
        cargarMonedas();
    }, []);

    const cargarBancos = async () => {
        try {
            const response = await axios.get('/api/bancos');
            setBancos(response.data);
        } catch (error) {
            console.error('Error al cargar bancos:', error);
            alert('Error al cargar los bancos');
        }
    };

    const cargarCuentasImputables = async () => {
        try {
            const response = await axios.get('/api/cuentas/imputables');
            setCuentasContables(response.data);
        } catch (error) {
            console.error('Error al cargar cuentas imputables:', error);
            alert('Error al cargar las cuentas contables');
        }
    };

    const cargarMonedas = async () => {
        try {
            const response = await axios.get('/api/monedas');
            setMonedas(response.data);
        } catch (error) {
            console.error('Error al cargar monedas:', error);
            alert('Error al cargar las monedas');
        }
    };

    const validarFormulario = () => {
        const nuevosErrores = {};

        // Validar ID Banco
        if (!formulario.idBanco.trim()) {
            nuevosErrores.idBanco = 'El ID del banco es requerido';
        }

        // Validar Nro Cuenta (debe seleccionar una cuenta imputable)
        if (!formulario.nroCta) {
            nuevosErrores.nroCta = 'Debe seleccionar una cuenta contable';
        }

        // Validar Nombre del Banco
        if (!formulario.nomBco.trim()) {
            nuevosErrores.nomBco = 'El nombre del banco es requerido';
        } else if (formulario.nomBco.trim().length > 30) {
            nuevosErrores.nomBco = 'El nombre del banco no puede tener más de 30 caracteres';
        }

        // Validar Nombre de la Cuenta
        if (!formulario.nomCta.trim()) {
            nuevosErrores.nomCta = 'El nombre de la cuenta es requerido';
        } else if (formulario.nomCta.trim().length > 30) {
            nuevosErrores.nomCta = 'El nombre de la cuenta no puede tener más de 30 caracteres';
        }

        // Validar Moneda
        if (!formulario.idMoneda) {
            nuevosErrores.idMoneda = 'Debe seleccionar una moneda';
        }

        // Verificar duplicados solo al crear
        if (!modoEdicion) {
            const duplicado = bancos.find(b => b.idBanco === formulario.idBanco.trim());
            if (duplicado) {
                nuevosErrores.idBanco = 'Ya existe un banco con ese ID';
            }
        }

        setErrores(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormulario(prev => ({
            ...prev,
            [name]: value
        }));

        // Limpiar error del campo que se está editando
        if (errores[name]) {
            setErrores(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validarFormulario()) {
            return;
        }

        try {
            const datosEnviar = {
                idBanco: formulario.idBanco.trim(),
                nroCta: formulario.nroCta,
                nomBco: formulario.nomBco.trim(),
                nomCta: formulario.nomCta.trim(),
                idMoneda: formulario.idMoneda,
                saldo: parseFloat(formulario.saldo) || 0,
                obs: formulario.obs.trim() || null,
                idOperador: usuario || null,
                fchultmod: new Date().toISOString()
            };

            if (modoEdicion) {
                await axios.put(`/api/bancos/${formulario.idBanco}`, datosEnviar);
                alert('Banco actualizado exitosamente');
            } else {
                await axios.post('/api/bancos', datosEnviar);
                alert('Banco creado exitosamente');
            }

            cerrarModal();
            cargarBancos();
        } catch (error) {
            console.error('Error al guardar banco:', error);
            alert('Error al guardar el banco: ' + (error.response?.data?.mensaje || error.message));
        }
    };

    const handleEditar = (banco) => {
        setFormulario({
            idBanco: banco.idBanco,
            nroCta: banco.nroCta || '',
            nomBco: banco.nomBco,
            nomCta: banco.nomCta,
            idMoneda: banco.idMoneda || '',
            saldo: banco.saldo?.toString() || '0.00',
            obs: banco.obs || '',
            idOperador: banco.idOperador || usuario || ''
        });
        setModoEdicion(true);
        setMostrarModal(true);
        setErrores({});
    };

    const handleEliminar = async (idBanco) => {
        if (window.confirm(`¿Está seguro de eliminar el banco ${idBanco}?`)) {
            try {
                await axios.delete(`/api/bancos/${idBanco}`);
                alert('Banco eliminado exitosamente');
                cargarBancos();
            } catch (error) {
                console.error('Error al eliminar banco:', error);
                alert('Error al eliminar el banco');
            }
        }
    };

    const abrirModalNuevo = () => {
        setFormulario({
            idBanco: '',
            nroCta: '',
            nomBco: '',
            nomCta: '',
            idMoneda: '',
            saldo: '0.00',
            obs: '',
            idOperador: usuario || ''
        });
        setModoEdicion(false);
        setMostrarModal(true);
        setErrores({});
    };

    const cerrarModal = () => {
        setMostrarModal(false);
        setFormulario({
            idBanco: '',
            nroCta: '',
            nomBco: '',
            nomCta: '',
            idMoneda: '',
            saldo: '0.00',
            obs: '',
            idOperador: usuario || ''
        });
        setModoEdicion(false);
        setErrores({});
    };

    const exportarExcel = () => {
        const datosExportar = bancosFiltrados.map(banco => ({
            'ID Banco': banco.idBanco,
            'Nro Cuenta': banco.nroCta,
            'Nombre Banco': banco.nomBco,
            'Nombre Cuenta': banco.nomCta,
            'Moneda': banco.idMoneda,
            'Saldo': banco.saldo,
            'Observaciones': banco.obs,
            'Operador': banco.idOperador
        }));

        const ws = XLSX.utils.json_to_sheet(datosExportar);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Bancos');
        XLSX.writeFile(wb, 'bancos.xlsx');
    };

    const bancosFiltrados = bancos.filter(banco =>
        banco.idBanco.toLowerCase().includes(busqueda.toLowerCase()) ||
        banco.nomBco.toLowerCase().includes(busqueda.toLowerCase()) ||
        banco.nomCta.toLowerCase().includes(busqueda.toLowerCase())
    );

    const obtenerDescripcionCuenta = (nroCta) => {
        const cuenta = cuentasContables.find(c => c.cuenta === nroCta);
        return cuenta ? cuenta.descripcion : nroCta;
    };

    const obtenerNombreMoneda = (idMoneda) => {
        const moneda = monedas.find(m => m.idMoneda === idMoneda);
        return moneda ? moneda.nomMoneda : idMoneda;
    };

    return (
        <div className="present-container">
            <div className="header-seccion">
                <h2>Gestión de Bancos</h2>
                <div className="acciones-header">
                    <input
                        type="text"
                        className="input-busqueda"
                        placeholder="Buscar banco..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />
                    <button className="btn btn-nuevo" onClick={abrirModalNuevo}>
                        ➕ Nuevo Banco
                    </button>
                    <button className="btn btn-exportar" onClick={exportarExcel}>
                        📊 Exportar a Excel
                    </button>
                </div>
            </div>

            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead>
                        <tr>
                            <th>ID Banco</th>
                            <th>Cuenta Contable</th>
                            <th>Nombre Banco</th>
                            <th>Nombre Cuenta</th>
                            <th>Moneda</th>
                            <th>Saldo</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bancosFiltrados.length > 0 ? (
                            bancosFiltrados.map(banco => (
                                <tr key={banco.idBanco}>
                                    <td>{banco.idBanco}</td>
                                    <td>{obtenerDescripcionCuenta(banco.nroCta)}</td>
                                    <td>{banco.nomBco}</td>
                                    <td>{banco.nomCta}</td>
                                    <td>{obtenerNombreMoneda(banco.idMoneda)}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        {parseFloat(banco.saldo).toLocaleString('es-ES', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </td>
                                    <td>
                                        <button
                                            className="btn-accion"
                                            onClick={() => handleEditar(banco)}
                                            title="Editar"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            className="btn-accion"
                                            onClick={() => handleEliminar(banco.idBanco)}
                                            title="Eliminar"
                                        >
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="sin-datos">
                                    No se encontraron bancos
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="footer-tabla">
                Total de bancos: {bancosFiltrados.length}
            </div>

            {mostrarModal && (
                <div className="modal-overlay">
                    <div className="modal-contenido">
                        <div className="modal-header">
                            <h3>{modoEdicion ? 'Editar Banco' : 'Nuevo Banco'}</h3>
                            <button className="btn-cerrar" onClick={cerrarModal}>×</button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>ID Banco *</label>
                                <input
                                    type="text"
                                    name="idBanco"
                                    className={`form-input ${errores.idBanco ? 'form-input-error' : ''}`}
                                    value={formulario.idBanco}
                                    onChange={handleChange}
                                    disabled={modoEdicion}
                                />
                                {errores.idBanco && <span className="form-error">{errores.idBanco}</span>}
                            </div>

                            <div className="form-group">
                                <label>Cuenta Contable *</label>
                                <select
                                    name="nroCta"
                                    className={`form-input ${errores.nroCta ? 'form-input-error' : ''}`}
                                    value={formulario.nroCta}
                                    onChange={handleChange}
                                >
                                    <option value="">Seleccione una cuenta...</option>
                                    {cuentasContables.map(cuenta => (
                                        <option key={cuenta.cuenta} value={cuenta.cuenta}>
                                            {cuenta.cuenta} - {cuenta.descripcion}
                                        </option>
                                    ))}
                                </select>
                                {errores.nroCta && <span className="form-error">{errores.nroCta}</span>}
                            </div>

                            <div className="form-group">
                                <label>Nombre del Banco *</label>
                                <input
                                    type="text"
                                    name="nomBco"
                                    className={`form-input ${errores.nomBco ? 'form-input-error' : ''}`}
                                    value={formulario.nomBco}
                                    onChange={handleChange}
                                    maxLength="30"
                                />
                                {errores.nomBco && <span className="form-error">{errores.nomBco}</span>}
                            </div>

                            <div className="form-group">
                                <label>Nombre de la Cuenta *</label>
                                <input
                                    type="text"
                                    name="nomCta"
                                    className={`form-input ${errores.nomCta ? 'form-input-error' : ''}`}
                                    value={formulario.nomCta}
                                    onChange={handleChange}
                                    maxLength="30"
                                />
                                {errores.nomCta && <span className="form-error">{errores.nomCta}</span>}
                            </div>

                            <div className="form-group">
                                <label>Moneda *</label>
                                <select
                                    name="idMoneda"
                                    className={`form-input ${errores.idMoneda ? 'form-input-error' : ''}`}
                                    value={formulario.idMoneda}
                                    onChange={handleChange}
                                >
                                    <option value="">Seleccione una moneda...</option>
                                    {monedas.map(moneda => (
                                        <option key={moneda.idMoneda} value={moneda.idMoneda}>
                                            {moneda.idMoneda} - {moneda.nomMoneda}
                                        </option>
                                    ))}
                                </select>
                                {errores.idMoneda && <span className="form-error">{errores.idMoneda}</span>}
                            </div>

                            <div className="form-group">
                                <label>Saldo</label>
                                <input
                                    type="number"
                                    name="saldo"
                                    className="form-input"
                                    value={formulario.saldo}
                                    onChange={handleChange}
                                    step="0.01"
                                />
                            </div>

                            <div className="form-group">
                                <label>Observaciones</label>
                                <textarea
                                    name="obs"
                                    className="form-input"
                                    value={formulario.obs}
                                    onChange={handleChange}
                                    rows="3"
                                    maxLength="255"
                                />
                            </div>

                            <div className="form-group">
                                <label>ID Operador (Usuario logueado)</label>
                                <input
                                    type="text"
                                    name="idOperador"
                                    className="form-input"
                                    value={formulario.idOperador}
                                    onChange={handleChange}
                                    disabled
                                    title="Este campo se establece automáticamente con el usuario logueado"
                                />
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-cancelar" onClick={cerrarModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-guardar">
                                    {modoEdicion ? 'Actualizar' : 'Guardar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BBanco;
